import { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, ChevronDown } from 'lucide-react';
import { PAPERS, IMAGES } from '../data/studio';
import { Reveal, Eyebrow } from './Reveal';

const NODES = [
  { x: 20, y: 30, r: 5, c: '#8B5CF6' },
  { x: 45, y: 15, r: 3.5, c: '#22D3EE' },
  { x: 70, y: 35, r: 6, c: '#F59E0B' },
  { x: 30, y: 65, r: 4, c: '#22D3EE' },
  { x: 60, y: 70, r: 5, c: '#8B5CF6' },
  { x: 82, y: 60, r: 3, c: '#10B981' },
  { x: 50, y: 45, r: 7, c: '#22D3EE' },
];

const LINKS = [[0, 1], [1, 6], [6, 2], [0, 3], [3, 6], [6, 4], [4, 5], [2, 5], [3, 4]];

export default function ResearchPortal() {
  const [openPaper, setOpenPaper] = useState(null);

  return (
    <section id="research" className="relative py-28 lg:py-40 nebula-mesh" data-testid="research-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Eyebrow accent="#8B5CF6">Cosmos &amp; Human · Research Portal</Eyebrow>
          <h2 className="font-celestial text-3xl sm:text-4xl lg:text-5xl font-light text-slate-100 max-w-3xl leading-tight">
            Where the <span className="text-violet-300">cell</span> meets the <span className="text-cyan-300">void</span>
          </h2>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-5 gap-8">
          <Reveal className="lg:col-span-2">
            <div className="relative h-full min-h-[420px] rounded-2xl overflow-hidden border border-white/[0.07]" data-testid="research-visualizer">
              <img src={IMAGES.synapse} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#02040a] via-[#02040a]/40 to-transparent" />
              <svg viewBox="0 0 100 90" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
                {LINKS.map(([a, b], i) => (
                  <motion.line
                    key={`link-${a}-${b}`}
                    x1={NODES[a].x} y1={NODES[a].y} x2={NODES[b].x} y2={NODES[b].y}
                    stroke="rgba(34,211,238,0.25)"
                    strokeWidth="0.2"
                    initial={{ pathLength: 0 }}
                    whileInView={{ pathLength: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.4, delay: i * 0.12 }}
                  />
                ))}
                {NODES.map((n, i) => (
                  <motion.circle
                    key={`node-${n.x}-${n.y}`}
                    cx={n.x} cy={n.y} r={n.r * 0.45}
                    fill={n.c}
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: [0.4, 1, 0.6], scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, delay: 0.4 + i * 0.1 }}
                    style={{ filter: `drop-shadow(0 0 6px ${n.c})` }}
                  />
                ))}
              </svg>
              <div className="absolute bottom-0 left-0 right-0 p-7">
                <p className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-cyan-400/80">Synapse · Cosmos node map</p>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">Cross-disciplinary links between the Human and Cosmos worlds, rendered as a living network.</p>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-3 space-y-4">
            {PAPERS.map((p, i) => {
              const open = openPaper === p.id;
              return (
                <Reveal key={p.id} delay={i * 0.1}>
                  <div
                    className={`rounded-2xl border bg-gradient-to-b from-slate-900/60 to-slate-950/80 transition-colors duration-500 ${open ? 'border-violet-400/40' : 'border-white/[0.07] hover:border-white/20'}`}
                    data-testid={`paper-card-${p.id}`}
                  >
                    <button
                      className="w-full text-left p-6 lg:p-7 flex items-start gap-5"
                      onClick={() => setOpenPaper(open ? null : p.id)}
                      data-testid={`paper-toggle-${p.id}`}
                    >
                      <FileText size={20} className="shrink-0 mt-1 text-violet-300" strokeWidth={1.5} />
                      <div className="flex-1">
                        <h3 className="text-base sm:text-lg text-slate-100 leading-snug font-medium">{p.title}</h3>
                        <p className="font-mono-precision text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-2">
                          {p.journal} · {p.year} · {p.citations} citations
                        </p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {p.tags.map((t) => (
                            <span key={t} className="font-mono-precision text-[10px] tracking-[0.15em] text-violet-300/80 border border-violet-400/20 rounded-full px-3 py-0.5">
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                      <ChevronDown size={18} className={`shrink-0 mt-1 text-slate-500 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
                    </button>
                    <motion.div
                      initial={false}
                      animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }}
                      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 lg:px-7 pb-7 pl-[68px] text-sm text-slate-400 leading-relaxed" data-testid={`paper-abstract-${p.id}`}>
                        {p.abstract}
                      </p>
                    </motion.div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
